import { useState } from "react";

import type { ProgressionPoint } from "../../../shared/src";

const SESSIONS = 5;
const WIDTH = 120;
const HEIGHT = 28;
const PAD = 4;

interface Props {
  points: ProgressionPoint[];
  currentWorkoutId: number;
}

interface Session {
  label: string;
  e1rm: number;
  volume: number;
}

/**
 * Two stacked sparklines (e1RM, volume) for the exercise's last 5 finished
 * sessions. Kept as plain SVG so the workout page doesn't pull in Recharts.
 * Separate charts rather than one dual-axis chart: the metrics differ by
 * orders of magnitude.
 */
export function ExerciseTrend({ points, currentWorkoutId }: Props) {
  const [active, setActive] = useState<number | null>(null);

  const sessions: Session[] = points
    .filter((p) => p.workout_id !== currentWorkoutId)
    .slice(-SESSIONS)
    .map((p) => ({
      label: formatDay(p.finished_at),
      e1rm: p.best_set.e1rm,
      volume: p.volume,
    }));

  if (sessions.length === 0) return null;

  return (
    <div
      role="group"
      aria-label="recent sessions trend"
      className="w-32 shrink-0 space-y-1"
    >
      <Sparkline
        name="e1RM"
        testId="e1rm-readout"
        sessions={sessions}
        value={(s) => s.e1rm}
        active={active}
        onSelect={setActive}
      />
      <Sparkline
        name="Volume"
        testId="volume-readout"
        sessions={sessions}
        value={(s) => s.volume}
        active={active}
        onSelect={setActive}
      />
      <p
        data-testid="trend-caption"
        className="text-right text-[11px] leading-4 text-slate-500"
      >
        {active !== null
          ? sessions[active]?.label
          : sessions.length === 1
            ? "Last session"
            : `Last ${sessions.length} sessions`}
      </p>
    </div>
  );
}

interface SparklineProps {
  name: string;
  testId: string;
  sessions: Session[];
  value: (s: Session) => number;
  active: number | null;
  onSelect: (index: number | null) => void;
}

function Sparkline({
  name,
  testId,
  sessions,
  value,
  active,
  onSelect,
}: SparklineProps) {
  const values = sessions.map(value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const x = (i: number) =>
    sessions.length === 1
      ? WIDTH / 2
      : PAD + (i * (WIDTH - 2 * PAD)) / (sessions.length - 1);
  // A flat series sits mid-height instead of hugging the floor.
  const y = (v: number) =>
    span === 0
      ? HEIGHT / 2
      : HEIGHT - PAD - ((v - min) * (HEIGHT - 2 * PAD)) / span;

  const shown = active ?? sessions.length - 1;
  const readout = `${formatKg(values[shown] ?? 0)} kg`;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-1 text-[11px] leading-4">
        <span className="text-slate-500">{name}</span>
        <span data-testid={testId} className="truncate text-slate-200">
          {readout}
        </span>
      </div>
      <svg
        role="img"
        aria-label={`${name} trend`}
        width={WIDTH}
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="block overflow-visible"
        onPointerLeave={(e) => e.pointerType === "mouse" && onSelect(null)}
      >
        {sessions.length > 1 && (
          <polyline
            points={values.map((v, i) => `${x(i)},${y(v)}`).join(" ")}
            fill="none"
            stroke="#f97316"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
        {values.map((v, i) => (
          <g key={i}>
            <circle
              data-point
              cx={x(i)}
              cy={y(v)}
              r={i === shown ? 4 : 2}
              fill="#f97316"
              stroke="#0f172a"
              strokeWidth={i === shown ? 2 : 0}
            />
            <rect
              role="button"
              aria-label={`${name} ${sessions[i]?.label}: ${formatKg(v)} kg`}
              x={x(i) - 10}
              y={0}
              width={20}
              height={HEIGHT}
              fill="transparent"
              className="cursor-pointer"
              // Hover preview is mouse-only: a touch tap also emits
              // enter, which would make the click below toggle it off.
              onPointerEnter={(e) => e.pointerType === "mouse" && onSelect(i)}
              onClick={() => onSelect(active === i ? null : i)}
            />
          </g>
        ))}
      </svg>
      <ul aria-label={`${name} by session`} className="sr-only">
        {sessions.map((s, i) => (
          <li key={i}>{`${s.label}: ${formatKg(values[i] ?? 0)} kg`}</li>
        ))}
      </ul>
    </div>
  );
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

function formatKg(n: number): string {
  return String(Math.round(n * 10) / 10);
}
